/**
 * Employee-dashboard breadcrumb origins. Built on the generic `from`-origin helpers in
 * `breadcrumbOrigin.ts`; this module only owns the employee-dashboard-specific matcher.
 *
 * When a user drills from another employee's dashboard into that employee's Goals, Reviews, Feedback,
 * or Documents, those pages root their breadcrumb on "{Employee}'s Dashboard", carried in the `from`
 * query param. Per the breadcrumb-origin contract, the employee's display name comes from the
 * destination page's own data (re-fetched via the id parsed out of `from`) — only the `from`
 * href travels.
 *
 * Dashboard origins deliberately bypass the fresh-tab gate — the "{Employee}'s Dashboard" root
 * must survive a module opening the page in a new tab.
 */
import type { Breadcrumb } from "@performyard/py-components";

import { getSanitizedFromEvenInFreshTab, nestedFromChain } from "./breadcrumbOrigin";

export { appendCurrentLocationAsFrom } from "./breadcrumbOrigin";

const EMPLOYEE_DASHBOARD_ID_PATTERN = /^\/(?:user|employee-page)\/([0-9a-fA-F]{24})(?=[/?]|$)/;

export type EmployeeDashboardOrigin = {
	/** Sanitized internal `from` path back to the originating employee dashboard. */
	from: string;
	/** The employee id parsed out of `from`, used to re-fetch the employee's name. */
	employeeId: string;
};

const matchEmployeeDashboardFrom = (from: string, currentUserId?: string): EmployeeDashboardOrigin | null => {
	if (!from) return null;
	const employeeId = from.match(EMPLOYEE_DASHBOARD_ID_PATTERN)?.[1];
	if (!employeeId) return null;
	if (currentUserId && employeeId === currentUserId) return null;
	return { from, employeeId };
};

/**
 * Resolve an employee-dashboard origin from the destination page's search string. Returns null
 * when there is no dashboard `from`, or when the dashboard belongs to the current user (own
 * dashboard keeps the plain "Dashboard" crumb).
 */
export const parseEmployeeDashboardOrigin = (search: string, currentUserId?: string): EmployeeDashboardOrigin | null =>
	matchEmployeeDashboardFrom(getSanitizedFromEvenInFreshTab(search), currentUserId);

export type EmployeeDashboardDeepOrigin = EmployeeDashboardOrigin & {
	/**
	 * When the origin was reached through an intermediate list page (e.g. dashboard →
	 * employee goals list → goal), the href back to that list; null for a direct origin.
	 */
	viaHref: string | null;
};

/**
 * Like `parseEmployeeDashboardOrigin`, but also finds an origin nested at any depth inside an
 * intermediate page's `from`. Also returns the href of the page the user came from directly.
 */
export const parseEmployeeDashboardOriginDeep = (
	search: string,
	currentUserId?: string
): EmployeeDashboardDeepOrigin | null => {
	const direct = parseEmployeeDashboardOrigin(search, currentUserId);
	if (direct) return { ...direct, viaHref: null };

	const viaHref = getSanitizedFromEvenInFreshTab(search);
	for (const hop of nestedFromChain(viaHref, { evenInFreshTab: true })) {
		const nested = matchEmployeeDashboardFrom(hop, currentUserId);
		if (nested) return { ...nested, viaHref };
	}
	return null;
};

/** The root "{Employee}'s Dashboard" crumb pointing back to the originating dashboard. */
export const employeeDashboardRootCrumb = (from: string, employeeName: string): Breadcrumb => ({
	pageId: "breadcrumb-employee-dashboard",
	name: `${employeeName}'s Dashboard`,
	href: from,
	forceHardReload: true,
});

export type EmployeeDashboardBreadcrumbLeaf = "Goals" | "Team Goals" | "Reviews" | "Feedback" | "Documents";

type BuildEmployeeDashboardBreadcrumbsArgs = {
	/** Href back to the originating dashboard (the origin's `from`). */
	from: string;
	/** Display name of the employee whose Goals/Reviews/Feedback/Documents are being viewed. */
	employeeName: string;
	/** The current page's leaf noun. */
	leafLabel: EmployeeDashboardBreadcrumbLeaf;
};

/**
 * Build the two-crumb chain for an employee's Goals/Reviews/Feedback/Documents list pages:
 * `{employeeName}'s Dashboard > {employeeName}'s {leafLabel}`. The leaf is the current page
 * (no link).
 */
export const buildEmployeeDashboardBreadcrumbs = ({
	from,
	employeeName,
	leafLabel,
}: BuildEmployeeDashboardBreadcrumbsArgs): Breadcrumb[] => [
	employeeDashboardRootCrumb(from, employeeName),
	{ pageId: "breadcrumb-employee-dashboard-leaf", name: `${employeeName}'s ${leafLabel}`, href: "" },
];
